import type { ServiceArea } from "../serviceAreas";

export const paAreas: ServiceArea[] = [
  {
    slug: "king-of-prussia-pa",
    city: "King of Prussia",
    state: "PA",
    county: "Montgomery County",
    title: "King of Prussia Limo & PHL Airport Car Service",
    description:
      "Chauffeured limo and black car service in King of Prussia, PA. Transfers to PHL, EWR and JFK, corporate travel, weddings and mall pickups. Call (929) 452-6031.",
    h1: "Limo Service in King of Prussia, PA",
    tagline:
      "Executive sedans, SUVs and Sprinters for the offices, hotels and shopping around the King of Prussia interchange.",
    intro: [
      "King of Prussia sits where the Schuylkill Expressway (I-76), US-202, US-422 and the Pennsylvania Turnpike come together, which is why so many regional offices, hotels and conference events land here. Our chauffeurs plan around the Schuylkill's rush-hour backups and the Turnpike interchange so that a 6 a.m. flight or a client pickup at a Mall Boulevard hotel runs on schedule.",
      "We come in from our Glassboro, NJ base across the Delaware River, so King of Prussia trips are booked ahead rather than dispatched on the spot. That suits the work we do most here: PHL transfers for business travelers, executive shuttles between Upper Merion offices and Center City, and wedding parties heading to venues around Valley Forge.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 25–40 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 85–105 min" },
      { code: "JFK", label: "JFK International", time: "About 110–140 min" },
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 105–135 min" },
    ],
    landmarks: [
      "King of Prussia Mall",
      "Valley Forge National Historical Park",
      "Valley Forge Casino Resort",
      "King of Prussia Town Center",
      "Mall Boulevard and First Avenue hotels",
      "US-202 and I-76 interchange",
    ],
    neighborhoods: [
      "Upper Merion Township",
      "Wayne",
      "Bridgeport",
      "Gulph Mills",
      "Conshohocken",
      "Audubon",
    ],
    popular: [
      {
        label: "PHL airport transfers",
        path: "/services/airport-transportation/phl",
        blurb:
          "Flight-tracked runs from King of Prussia hotels and homes to PHL via I-76 or I-476, timed to the Schuylkill's traffic.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Executive rides between First Avenue offices, client sites and Center City, with a chauffeur who waits between meetings.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Bridal party and guest shuttles for receptions near Valley Forge Park and the Main Line.",
      },
      {
        label: "Hourly service",
        path: "/services/hourly-as-directed",
        blurb:
          "Keep a car on call for a full day of shopping at the mall or a run of back-to-back site visits.",
      },
    ],
    faqs: [
      {
        q: "Can you pick up at King of Prussia Mall?",
        a: "Yes. The mall has many entrances across its connected wings, so we agree on a specific door or department-store entrance before the day and send the chauffeur's details in advance.",
      },
      {
        q: "Will my trip use the Pennsylvania Turnpike?",
        a: "It depends on the destination. Newark and New York trips usually use the Turnpike, and any tolls are confirmed in your quote before you travel.",
      },
      {
        q: "Is PHL or Newark the better airport from King of Prussia?",
        a: "PHL is usually well under an hour, while Newark is closer to two. If your flight only leaves from EWR, JFK or LaGuardia, we plan the pickup with extra buffer for New Jersey and New York traffic.",
      },
      {
        q: "Can you move a team to a Valley Forge conference?",
        a: "Yes. Tell us the head count and luggage, and we will suggest SUVs or a Sprinter van and schedule arrivals and departures around the event agenda.",
      },
    ],
    nearby: ["philadelphia-pa", "media-pa", "west-chester-pa", "bensalem-pa"],
  },
  {
    slug: "bensalem-pa",
    city: "Bensalem",
    state: "PA",
    county: "Bucks County",
    title: "Bensalem Limo Service & Parx Casino Car Service",
    description:
      "Limo and black car service in Bensalem, PA. Rides to PHL, EWR and JFK, Parx Casino nights, weddings and corporate trips across lower Bucks. Call (929) 452-6031.",
    h1: "Limo Service in Bensalem, PA",
    tagline:
      "Chauffeured rides from lower Bucks County to the airports, Parx Casino and Northeast Philadelphia.",
    intro: [
      "Bensalem is boxed in by I-95, US-1 (Roosevelt Boulevard), Street Road and the Pennsylvania Turnpike, which puts it within reach of both Philadelphia and the New Jersey Turnpike. That location makes it a natural starting point for Newark and New York flights as well as PHL, and our chauffeurs choose the route based on the time of day rather than habit.",
      "Our cars cross from Glassboro, NJ, so we ask for Bensalem bookings in advance. Most requests here are casino nights at Parx, hotel pickups along Street Road and US-1, family trips to Sesame Place in neighboring Langhorne, and corporate travelers staying near the Trevose office parks.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 30–45 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 60–80 min" },
      { code: "JFK", label: "JFK International", time: "About 95–125 min" },
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 90–115 min" },
    ],
    landmarks: [
      "Parx Casino and Parx Racing",
      "Neshaminy Mall",
      "Neshaminy State Park",
      "Andalusia Historic House and Gardens",
      "Cornwells Heights SEPTA and Amtrak station",
      "Street Road and US-1 hotel corridor",
    ],
    neighborhoods: [
      "Cornwells Heights",
      "Trevose",
      "Andalusia",
      "Eddington",
      "Feasterville",
      "Croydon",
    ],
    popular: [
      {
        label: "Night on the town",
        path: "/services/night-on-the-town",
        blurb:
          "Dinner and an evening at Parx with a chauffeur waiting at the entrance when you are ready to leave.",
      },
      {
        label: "Newark airport transfers",
        path: "/services/airport-transportation/ewr",
        blurb:
          "Turnpike-to-Turnpike runs to EWR, often the quickest New York-area airport from lower Bucks County.",
      },
      {
        label: "Birthdays",
        path: "/services/birthdays",
        blurb:
          "Birthday groups to Philadelphia restaurants, Parx shows or a family day out, with one pickup and one ride home.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Point-to-point service for Trevose and Bensalem business parks, hotels and client offices in Philadelphia.",
      },
    ],
    faqs: [
      {
        q: "Can you drop off and pick up at Parx Casino?",
        a: "Yes. We use the main entrance drop-off and agree on a return time, or keep the car on hourly standby if you would rather leave whenever you choose.",
      },
      {
        q: "Should I fly from PHL or Newark when leaving from Bensalem?",
        a: "Both are realistic. PHL is usually closer, but I-95 through Philadelphia can be slow at rush hour, and Newark is a straightforward Turnpike run. We will suggest a pickup time for whichever airport your flight uses.",
      },
      {
        q: "Are Turnpike and bridge tolls part of the price?",
        a: "Tolls for the route we plan are listed in your quote, so you know the full cost before you confirm the ride.",
      },
      {
        q: "Can a group of twelve ride together to Sesame Place or a game?",
        a: "Yes. A Mercedes Sprinter usually fits a group that size with room for bags; larger groups can be split across vehicles or quoted as a party bus request.",
      },
    ],
    nearby: ["philadelphia-pa", "doylestown-pa", "trenton-nj", "princeton-nj"],
  },
  {
    slug: "media-pa",
    city: "Media",
    state: "PA",
    county: "Delaware County",
    title: "Media PA Limo Service & Airport Transfers",
    description:
      "Chauffeured car service in Media, PA and central Delaware County. Transfers to PHL, EWR and JFK, courthouse trips, weddings and proms. Call (929) 452-6031.",
    h1: "Limo Service in Media, PA",
    tagline:
      "Private chauffeured rides for Delaware County's county seat, from State Street to PHL.",
    intro: [
      "Media is the Delaware County seat, and much of its weekday traffic revolves around the courthouse, law offices and Riddle Hospital. A scheduled car takes parking off the list for attorneys, jurors and visitors, and the drop-off on Front or State Street is confirmed ahead of time.",
      "The borough is linked to I-476 (the Blue Route) and I-95 by Baltimore Pike (US-1) and Route 252, so PHL is a relatively short run. We travel over from our Glassboro, NJ base via the Commodore Barry Bridge, and we plan Media pickups in advance for airport transfers, weddings at Delaware County estates and prom nights.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 20–35 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 90–110 min" },
      { code: "JFK", label: "JFK International", time: "About 120–150 min" },
    ],
    landmarks: [
      "Delaware County Courthouse",
      "Media Theatre on State Street",
      "Riddle Hospital",
      "Penn State Brandywine",
      "Tyler Arboretum",
      "Ridley Creek State Park",
    ],
    neighborhoods: [
      "Swarthmore",
      "Wallingford",
      "Rose Valley",
      "Springfield",
      "Upper Providence",
      "Glen Mills",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "PHL via the Blue Route and I-95, plus scheduled trips to Newark and JFK for international departures.",
      },
      {
        label: "Prom",
        path: "/services/prom",
        blurb:
          "Penncrest, Strath Haven and other area prom groups with photo stops and a return time set with parents.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Guest and bridal party transportation for ceremonies in Media and receptions at nearby estates and country clubs.",
      },
      {
        label: "Hourly service",
        path: "/services/hourly-as-directed",
        blurb:
          "A chauffeur on standby for a long court day, several appointments or a dinner and show at the Media Theatre.",
      },
    ],
    faqs: [
      {
        q: "Can you drop me at the Delaware County Courthouse?",
        a: "Yes. We schedule the drop-off with enough time for security screening and can wait or return at a set time for the ride home.",
      },
      {
        q: "How long does Media to PHL take?",
        a: "Usually 20 to 35 minutes depending on I-476 and I-95 traffic. Early-morning departures tend to be at the faster end.",
      },
      {
        q: "Do you bring cars over from New Jersey for Media pickups?",
        a: "Yes. Our base is in Glassboro, NJ, and Media is a regular run across the Commodore Barry Bridge. Any bridge tolls are confirmed in your quote.",
      },
      {
        q: "Can you handle an evening at the Media Theatre and dinner on State Street?",
        a: "Yes. We drop you near the theatre, then pick up at a time you choose so you are not looking for parking on a busy show night.",
      },
    ],
    nearby: ["west-chester-pa", "king-of-prussia-pa", "philadelphia-pa", "cherry-hill-nj"],
  },
  {
    slug: "west-chester-pa",
    city: "West Chester",
    state: "PA",
    county: "Chester County",
    title: "West Chester Limo & Brandywine Valley Car Service",
    description:
      "Limo and black car service in West Chester, PA. PHL, EWR and JFK transfers, WCU events, weddings and Brandywine Valley winery tours. Call (929) 452-6031.",
    h1: "Limo Service in West Chester, PA",
    tagline:
      "Chauffeured rides for the Chester County seat, West Chester University and the Brandywine Valley.",
    intro: [
      "West Chester mixes a lively downtown around High and Gay Streets with West Chester University, the Chester County Courthouse and a strong corporate presence along the US-202 corridor. Weekday trips tend to be business and court related; weekends lean toward university events, restaurant nights and weddings in the surrounding countryside.",
      "From here, PHL is reached by US-322 to I-95 or by US-202 and the Blue Route, and we choose between them based on traffic. Because we drive out from Glassboro, NJ, we schedule West Chester rides ahead of time, which works well for Brandywine Valley winery days, Longwood Gardens visits in nearby Kennett Square and family airport runs.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 35–50 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 100–120 min" },
      { code: "JFK", label: "JFK International", time: "About 130–160 min" },
    ],
    landmarks: [
      "West Chester University",
      "Historic Chester County Courthouse",
      "Chester County Hospital",
      "Gay Street and High Street restaurants",
      "American Helicopter Museum",
      "Longwood Gardens in nearby Kennett Square",
    ],
    neighborhoods: [
      "West Goshen",
      "East Bradford",
      "Exton",
      "Malvern",
      "Downingtown",
      "Chadds Ford",
    ],
    popular: [
      {
        label: "Wine and sightseeing",
        path: "/services/wine-and-sightseeing",
        blurb:
          "A chauffeured loop through Chadds Ford and Brandywine Valley wineries, with the car waiting at each stop.",
      },
      {
        label: "School events",
        path: "/services/school-events",
        blurb:
          "Rides for WCU move-in, parents weekends and commencement, with pickup points agreed around campus traffic.",
      },
      {
        label: "PHL airport transfers",
        path: "/services/airport-transportation/phl",
        blurb:
          "Scheduled runs from West Chester to PHL with luggage-ready SUVs and a buffer for US-322 or Blue Route delays.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Guest shuttles and bridal party cars for barn, estate and garden weddings across Chester County.",
      },
    ],
    faqs: [
      {
        q: "Can you plan a Brandywine Valley winery day from West Chester?",
        a: "Yes. Share the wineries you want to visit and a rough schedule, and we will quote hourly service so the same chauffeur stays with your group all day.",
      },
      {
        q: "How long is the drive from West Chester to PHL?",
        a: "Typically 35 to 50 minutes. Rush hour on US-202 or the Blue Route can stretch that, so we time the pickup to your flight.",
      },
      {
        q: "Do you provide transportation for West Chester University commencement?",
        a: "Yes. Graduation weekends book up early, so let us know your dates soon and we will confirm pickup points that avoid the busiest campus streets.",
      },
      {
        q: "Can you fit a wedding party of ten in one vehicle?",
        a: "A Mercedes Sprinter van usually seats a group that size comfortably. For larger guest counts we can quote several vehicles or a party bus request.",
      },
    ],
    nearby: ["media-pa", "king-of-prussia-pa", "philadelphia-pa"],
  },
  {
    slug: "doylestown-pa",
    city: "Doylestown",
    state: "PA",
    county: "Bucks County",
    title: "Doylestown Limo Service & Bucks County Wine Tours",
    description:
      "Chauffeured limo and car service in Doylestown, PA. Airport rides to PHL, EWR and JFK, weddings, Bucks County wine tours and events. Call (929) 452-6031.",
    h1: "Limo Service in Doylestown, PA",
    tagline:
      "Chauffeured sedans and Sprinters for central Bucks County weddings, wineries and airport trips.",
    intro: [
      "Doylestown is the Bucks County seat and one of the region's busiest wedding and day-trip towns. Visitors come for the Mercer Museum, Fonthill Castle and the Michener Art Museum, and couples book receptions at estates and inns across central Bucks, often with guests staying in hotels several miles away.",
      "US-202 and Route 611 are the main routes in and out, with Route 611 heading south toward Philadelphia and US-202 running east toward New Hope and New Jersey. We are based in Glassboro, NJ, so Doylestown rides are reserved in advance; in return you get a confirmed vehicle and chauffeur for guest shuttles, winery afternoons and early-morning airport runs.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 50–70 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 70–90 min" },
      { code: "JFK", label: "JFK International", time: "About 110–140 min" },
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 100–130 min" },
    ],
    landmarks: [
      "Mercer Museum",
      "Fonthill Castle",
      "Michener Art Museum",
      "Delaware Valley University",
      "Doylestown Hospital",
      "Bucks County Justice Center",
    ],
    neighborhoods: [
      "Doylestown Township",
      "Buckingham",
      "New Britain",
      "Furlong",
      "Warrington",
      "New Hope",
    ],
    popular: [
      {
        label: "Wine and sightseeing",
        path: "/services/wine-and-sightseeing",
        blurb:
          "Hourly service around Bucks County wineries and on to New Hope, with no one in the group needing to drive.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Hotel-to-venue guest shuttles and bridal party cars for inns, barns and estates in central Bucks.",
      },
      {
        label: "Bachelorette parties",
        path: "/services/bachelorette-party",
        blurb:
          "A winery afternoon or a New Hope evening for the bridal crew, with every stop planned before the day.",
      },
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Pickups timed for PHL, Newark, JFK or LaGuardia, chosen by whichever airport your airline uses.",
      },
    ],
    faqs: [
      {
        q: "Which airport is closest to Doylestown?",
        a: "Among the airports we serve, PHL and Newark are similar distances, often around an hour each depending on traffic. We recommend the one with the better flight for you and plan the pickup accordingly.",
      },
      {
        q: "Can you shuttle wedding guests between hotels and a Bucks County venue?",
        a: "Yes. We schedule loops between the hotel and venue around the ceremony and reception, and confirm drop-off points with the venue in advance.",
      },
      {
        q: "How many people fit for a Bucks County winery tour?",
        a: "Sedans and SUVs suit small groups; a Mercedes Sprinter takes a larger party. Tell us the head count and we will match the vehicle.",
      },
      {
        q: "Do you pick up at the Mercer Museum or Fonthill Castle?",
        a: "Yes. Both are common sightseeing stops, and we agree on a pickup spot by the entrance so your group is not walking the lot.",
      },
    ],
    nearby: ["bensalem-pa", "allentown-pa", "princeton-nj", "trenton-nj"],
  },
  {
    slug: "allentown-pa",
    city: "Allentown",
    state: "PA",
    county: "Lehigh County",
    title: "Allentown Limo Service & Newark Airport Transfers",
    description:
      "Chauffeured car service in Allentown and the Lehigh Valley, PA. Transfers to EWR, PHL and JFK, PPL Center events and corporate rides. Call (929) 452-6031.",
    h1: "Limo Service in Allentown, PA",
    tagline:
      "Pre-booked chauffeured rides from the Lehigh Valley to Newark, Philadelphia and New York.",
    intro: [
      "Allentown is the largest city in the Lehigh Valley, and many of its travelers fly out of Newark or Philadelphia for nonstop and international flights. I-78 runs east to Newark Liberty, while the Pennsylvania Turnpike Northeast Extension (I-476) heads south toward PHL, so both airports are a predictable drive with a professional at the wheel.",
      "This is the farthest Pennsylvania city on our regular list from our Glassboro, NJ base, so we ask for Allentown trips to be booked with notice. That works well for planned travel: airport transfers, executive rides to offices around Hamilton Street, concerts and hockey at the PPL Center, and group outings to Dorney Park or Wind Creek in Bethlehem.",
    ],
    airports: [
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 70–90 min" },
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 75–95 min" },
      { code: "JFK", label: "JFK International", time: "About 110–140 min" },
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 100–130 min" },
    ],
    landmarks: [
      "PPL Center",
      "Coca-Cola Park",
      "Dorney Park & Wildwater Kingdom",
      "Muhlenberg College",
      "Lehigh Valley Hospital–Cedar Crest",
      "Allentown Art Museum",
    ],
    neighborhoods: [
      "Downtown Allentown",
      "West End",
      "Whitehall",
      "Emmaus",
      "Bethlehem",
      "Fogelsville",
    ],
    popular: [
      {
        label: "Newark airport transfers",
        path: "/services/airport-transportation/ewr",
        blurb:
          "A straight I-78 run to EWR, scheduled with a traffic buffer for the approach through northern New Jersey.",
      },
      {
        label: "Concerts",
        path: "/services/concerts",
        blurb:
          "Drop-off and pickup for shows at the PPL Center, or a longer evening trip to a Philadelphia or New York venue.",
      },
      {
        label: "Sports events",
        path: "/services/sports-events",
        blurb:
          "Phantoms and IronPigs game nights locally, or a group ride to Philadelphia stadiums for the bigger matchups.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Executive transportation between Lehigh Valley offices, client meetings and the region's major airports.",
      },
    ],
    faqs: [
      {
        q: "Is Newark or Philadelphia the better airport from Allentown?",
        a: "Both are similar drives, usually 70 to 95 minutes. Newark via I-78 often has more nonstop and international options, while PHL via I-476 is handy for American Airlines connections. We plan around whichever you book.",
      },
      {
        q: "How far ahead should I book an Allentown pickup?",
        a: "As early as you can. Our cars come from Glassboro, NJ, so advance notice lets us confirm a vehicle and chauffeur for your exact pickup time.",
      },
      {
        q: "Are Turnpike tolls included in the quote?",
        a: "Tolls on I-476 and any New Jersey crossings for the planned route are shown in your quote before you confirm.",
      },
      {
        q: "Can you take a group to a PPL Center concert?",
        a: "Yes. A Sprinter van handles most groups, and we arrange a pickup point near the arena with a set return time after the show.",
      },
    ],
    nearby: ["doylestown-pa", "king-of-prussia-pa", "bensalem-pa"],
  },
];
